/**
 * Speed Presets Hook
 *
 * Configuration for all 6 speed presets from v8 prototype.
 * Each preset adjusts timing, visual intensity, and animation behavior.
 */

import { useMemo } from 'react';
import type { SpeedPreset, SpeedSettings } from '../types';

/**
 * Speed presets with speed-specific minimums for 2.5s emergence target at 3x.
 *
 * Each preset is tuned to feel distinctly different:
 * - Contemplative (1x): Full theatrical experience, meditative
 * - Relaxed (1.25x): Slightly faster than contemplative, still immersive
 * - Flowing (1.5x): Natural reading pace
 * - Purposeful (1.8x): Efficient but elegant
 * - Efficient (2x): Business-like, gets to the point
 * - Rapid (2.5x): Quick processing feel
 * - Express (3x): Snappy, production default
 */
export const SPEED_PRESETS: Record<SpeedPreset, SpeedSettings> = {
  '1': {
    multiplier: 1.0,
    label: 'Contemplative',
    bounceScale: 1.0,
    chromaOffset: 8,
    blurMax: 8,
    minLetter: 60,
    minWord: 80,
    minFlight: 120,
  },
  '1.25': {
    multiplier: 1.25,
    label: 'Relaxed',
    bounceScale: 1.0,
    chromaOffset: 8,
    blurMax: 8,
    minLetter: 55,
    minWord: 70,
    minFlight: 110,
  },
  '1.5': {
    multiplier: 1.5,
    label: 'Flowing',
    bounceScale: 1.0,
    chromaOffset: 8,
    blurMax: 8,
    minLetter: 50,
    minWord: 60,
    minFlight: 100,
  },
  '1.8': {
    multiplier: 1.8,
    label: 'Purposeful',
    bounceScale: 0.9,
    chromaOffset: 7,
    blurMax: 7,
    minLetter: 40,
    minWord: 50,
    minFlight: 90,
  },
  '2': {
    multiplier: 2.0,
    label: 'Efficient',
    bounceScale: 0.85,
    chromaOffset: 6,
    blurMax: 6,
    minLetter: 35,
    minWord: 40,
    minFlight: 80,
  },
  '2.5': {
    multiplier: 2.5,
    label: 'Rapid',
    bounceScale: 0.75,
    chromaOffset: 5,
    blurMax: 5,
    minLetter: 28,
    minWord: 30,
    minFlight: 70,
  },
  '3': {
    multiplier: 3.0,
    label: 'Express',
    bounceScale: 0.6,
    chromaOffset: 4,
    blurMax: 4,
    minLetter: 22,
    minWord: 20,
    minFlight: 60,
  },
};

/**
 * Base words for line (first sentence) based on speed.
 * Express (3x) uses fewer words to hit 2.5s emergence target.
 */
export function getWordsForLine(speed: SpeedPreset): number {
  const multiplier = SPEED_PRESETS[speed].multiplier;
  if (multiplier >= 3.0) return 4; // Express: ~18 letters
  if (multiplier >= 2.5) return 5; // Rapid: ~25 letters
  return 7; // Others: ~40 letters
}

/**
 * Scale a duration by the speed multiplier with a minimum floor.
 */
export function scaleDuration(
  baseDuration: number,
  minDuration: number,
  multiplier: number
): number {
  return Math.max(baseDuration / multiplier, minDuration);
}

/**
 * Hook to get current speed settings and helper functions.
 */
export function useSpeedPresets(speed: SpeedPreset = '3') {
  const settings = useMemo(() => SPEED_PRESETS[speed], [speed]);
  const wordsForLine = useMemo(() => getWordsForLine(speed), [speed]);

  /**
   * Get letter delay based on position in word/sentence.
   * First letters are slower for dramatic effect.
   */
  const getLetterDelay = useMemo(() => {
    return (letterIdx: number, wordIdx: number): number => {
      const { multiplier, minLetter } = settings;
      const isExpress = multiplier >= 3.0;

      // Base delays - Express uses faster base
      const firstLetterBase = isExpress ? 400 : 700;
      const subsequentBase = isExpress ? 250 : 450;
      const acceleration = isExpress ? 50 : 35;

      const baseDelay = letterIdx === 0 ? firstLetterBase : subsequentBase;
      const accelDelay = letterIdx * acceleration;

      return scaleDuration(baseDelay - accelDelay, minLetter, multiplier);
    };
  }, [settings]);

  /**
   * Get flight duration for letter animation.
   */
  const getFlightDuration = useMemo(() => {
    return (): number => {
      const { multiplier, minFlight } = settings;
      const isExpress = multiplier >= 3.0;
      const baseFlight = isExpress ? 300 : 450;

      return scaleDuration(baseFlight, minFlight, multiplier);
    };
  }, [settings]);

  /**
   * Get landing bounce duration.
   */
  const getLandingDuration = useMemo(() => {
    return (): number => {
      const { multiplier, minLetter } = settings;
      const isExpress = multiplier >= 3.0;
      const baseLanding = isExpress ? 180 : 300;

      return scaleDuration(baseLanding, minLetter * 0.8, multiplier);
    };
  }, [settings]);

  /**
   * Get pause duration between words.
   */
  const getInterWordPause = useMemo(() => {
    return (): number => {
      const { multiplier, minWord } = settings;
      const isExpress = multiplier >= 3.0;
      const basePause = isExpress ? 150 : 350;

      return scaleDuration(basePause, minWord, multiplier);
    };
  }, [settings]);

  return {
    settings,
    wordsForLine,
    getLetterDelay,
    getFlightDuration,
    getLandingDuration,
    getInterWordPause,
    scaleDuration: (base: number, min: number) =>
      scaleDuration(base, min, settings.multiplier),
  };
}

export type UseSpeedPresetsReturn = ReturnType<typeof useSpeedPresets>;
